// Field messages go out five times a second to every player watching a hunt, and each carried the whole scene:
// about 95% of what a hunting player was sent (530 KB a minute, 2026-10-25), though only one or two of a dozen
// wilds move or change in a tick. Now they carry what changed since the last one: every wild's uid in order, and
// whole only the wilds that are new or changed. The whole field still goes out after a hello or a sync, on a new
// hunt and every FIELD_FULL_MS, so a client can never drift for long.
// Nine in ten changed wilds had only walked (2026-10-25), yet went out whole (about 140 bytes): those now go as a
// uid and a spot (m), and every spot sent is to 0.01 tile (tile2), not the 17 digits of a float.
import type { FieldDTO, WildDTO } from './protocol.ts';

/** The whole field goes out at least this often. */
export const FIELD_FULL_MS = 10_000;

/** A spot on the hunt as sent: to 0.01 tile, far below a pixel. */
export const tile2 = (v: number): number => Math.round(v * 100) / 100;

/** What changed on the scene since the last field message. */
export interface FieldPatch {
  /** Every wild, in order, by uid. */
  ids: number[];
  /** The wilds that are new or changed (other than walking), whole. */
  w?: WildDTO[];
  /** The wilds that only walked: uid, x, y, uid, x, y... (a page from before these gets them in w: withoutMoves). */
  m?: number[];
  /** The rest only when changed (lead may become null). */
  lead?: FieldDTO['lead'];
  restingUntil?: number;
  teamHp?: FieldDTO['teamHp'];
}

/** Same flat object: the same keys holding the same values. */
function same(a: object | null | undefined, b: object | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) if ((a as Record<string, unknown>)[k] !== (b as Record<string, unknown>)[k]) return false;
  return true;
}

function sameTeam(a: FieldDTO['teamHp'], b: FieldDTO['teamHp']): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) if (!same(a[k as unknown as number], b[k as unknown as number])) return false;
  return true;
}

/** The same wild but for where it stands. */
function walkedOnly(a: WildDTO, b: WildDTO): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) if (k !== 'x' && k !== 'y' && (a as unknown as Record<string, unknown>)[k] !== (b as unknown as Record<string, unknown>)[k]) return false;
  return true;
}

/** What turns `prev` into `next`, on the same ground. */
export function fieldPatch(prev: FieldDTO, next: FieldDTO): FieldPatch {
  const before = new Map(prev.wilds.map((w) => [w.uid, w]));
  const p: FieldPatch = { ids: next.wilds.map((w) => w.uid) };
  const changed: WildDTO[] = [], moved: number[] = [];
  for (const w of next.wilds) {
    const was = before.get(w.uid);
    if (same(was, w)) continue;
    if (was && walkedOnly(was, w)) moved.push(w.uid, w.x, w.y);
    else changed.push(w);
  }
  if (changed.length) p.w = changed;
  if (moved.length) p.m = moved;
  if (!same(prev.lead, next.lead)) p.lead = next.lead;
  if (next.restingUntil !== prev.restingUntil) p.restingUntil = next.restingUntil;
  if (!sameTeam(prev.teamHp, next.teamHp)) p.teamHp = next.teamHp;
  return p;
}

/**
 * The field a patch makes of the one before it, as a new object (the old one stays as it was: the scene draws
 * between the two). Null when they don't fit, a wild it has never seen: the client keeps what it has until the
 * next whole field.
 */
export function applyFieldPatch(prev: FieldDTO, p: FieldPatch): FieldDTO | null {
  const known = new Map(prev.wilds.map((w) => [w.uid, w]));
  for (const w of p.w ?? []) known.set(w.uid, w);
  const m = p.m ?? [];
  for (let i = 0; i + 2 < m.length; i += 3) {
    const w = known.get(m[i]);
    if (!w) return null;
    known.set(w.uid, { ...w, x: m[i + 1], y: m[i + 2] });
  }
  const wilds: WildDTO[] = [];
  for (const uid of p.ids) {
    const w = known.get(uid);
    if (!w) return null;
    wilds.push(w);
  }
  return {
    ground: prev.ground,
    lead: p.lead !== undefined ? p.lead : prev.lead,
    wilds,
    restingUntil: p.restingUntil ?? prev.restingUntil,
    teamHp: p.teamHp ?? prev.teamHp,
  };
}

/** The patch for a page from before walk entries (Conn.moves): the wilds that walked go out whole in w. */
export function withoutMoves(p: FieldPatch, next: FieldDTO): FieldPatch {
  if (!p.m) return p;
  const walked = new Set<number>();
  for (let i = 0; i < p.m.length; i += 3) walked.add(p.m[i]);
  const { m: _, ...rest } = p;
  return { ...rest, w: [...(p.w ?? []), ...next.wilds.filter((w) => walked.has(w.uid))] };
}
